import React, { useState, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

// local component imports
import GridLottieComponent from "../../../../../components/atoms/gridLottie";
import CellCaption from "../CellCaption";
import CustomImage from "../../../../../components/atoms/customImage";
import VideoPlayer from "../../../../../components/atoms/videoPlayer";

// services and utils imports
import { getCellAccessibleLabel } from "../../../../../utils/dynamicHomeUtils/getCellAccessibleLabel";
import { useWidgetClick } from "../../../../../utils/dynamicHomeUtils/WidgetClickContext";
import { runOnNavigationIntent } from "../../../../../utils/common/navigationIntent";
import { setIsNavigatingAction } from "../../../../../store/slices/commonSlice";

/**
 * Renders a single grid cell — image, video, or Lottie animation — with optional overlay text and link.
 * @param {object} props - Component props
 * @param {object} props.cell - Cell data object
 * @param {number} props.index - Index of the cell in the grid
 * @param {Function} props.onVideoToggle - Callback to toggle video play/pause by index
 * @param {object} props.videoRefs - Ref map for video elements
 * @param {boolean} props.isEager - Whether this above-fold image should avoid lazy loading
 * @param {boolean} props.isPriority - Whether this image should use fetchPriority="high" (first 3 above-fold widgets)
 * @returns {React.ReactElement|null} Rendered grid cell
 */
const GridCell = ({
  cell,
  index,
  onVideoToggle,
  videoRefs,
  gridTextStyle,
  columnCount,
  isMobile,
  aspectRatio,
  cellBorderRadius,
  cellBorderStyle,
  cellBorderThickness,
  isEager = false,
  isPriority = false,
}) => {
  const dispatch = useDispatch();
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const localVideoRef = useRef(null);
  const { onCellClick } = useWidgetClick() ?? {};

  // TODO: NEED TO REMOVE THIS HACK ONCE CONTENT TEAM STARTS USING MEDIA TYPE PROPERLY
  const resolvedMediaType =
    cell.mediaType == "image" || cell.mediaType == undefined
      ? cell.mediaUrl?.includes(".mp4")
        ? "video"
        : "image"
      : cell.mediaType || "image";

  const imageUrl =
    cell.imageUrl || cell.mediaUrl || (cell.background?.type === "image" ? cell.background.value : undefined);

  const textContent = cell.textContent || cell.title;
  const colCount = columnCount ?? 1;
  const accessibleLabel = getCellAccessibleLabel(cell);
  const isImageCell = resolvedMediaType === "image";

  /** Reveals the video if it is already playing or has enough data buffered.
   * Covers the case where the video was already cached (e.g. reloading directly onto
   * this widget's viewport) and starts playing before React attaches its "playing"
   * listener — a race where both the loadeddata and playing events can be missed.
   */
  const revealIfPlayable = () => {
    const video = localVideoRef.current;
    if (video && (!video.paused || video.readyState >= 3)) setIsVideoLoaded(true);
  };

  // Keep the shared videoRefs map in sync
  useEffect(() => {
    if (localVideoRef.current) {
      videoRefs.current.set(index, localVideoRef.current);
      revealIfPlayable();
    }
    return () => videoRefs.current.delete(index);
  }, [index, videoRefs]);

  // Explicitly trigger play after hydration since autoplay attribute alone
  // is not reliable on initial page load in React SSR environments
  useEffect(() => {
    if (resolvedMediaType === "video" && localVideoRef.current) {
      localVideoRef.current.play().catch(() => {});
      revealIfPlayable();
      // Belt-and-braces: some browsers can start playing a cached video without
      // ever firing loadeddata or playing before React's listeners attach, so
      // give the element a brief window to be polled directly as a last resort.
      const pollId = setInterval(revealIfPlayable, 150);
      const stopId = setTimeout(() => clearInterval(pollId), 2000);
      return () => {
        clearInterval(pollId);
        clearTimeout(stopId);
      };
    }
  }, [resolvedMediaType]);

  const {
    textEnabled = true,
    innerText = true,
    fontSize: textFontSize,
    fontWeight: textFontWeight,
    fontFamily: textFontFamily,
    titleColor: textColor,
    horizontalAlignment = "center",
    verticalAlignment = "bottom",
  } = gridTextStyle || {};

  const textStyle = {
    fontSize: textFontSize ? `${textFontSize}px` : undefined,
    fontWeight: textFontWeight || undefined,
    fontFamily: textFontFamily || undefined,
    color: textColor || undefined,
    textAlign: horizontalAlignment,
  };

  const verticalPositionClass =
    verticalAlignment === "top" ? "top-0" : verticalAlignment === "center" ? "top-1/2 -translate-y-1/2" : "bottom-0";

  /** @returns {void} */
  const onLoadedData = useCallback(() => {
    setIsVideoLoaded(true);
    if (localVideoRef.current) {
      localVideoRef.current.play().catch(() => {});
    }
  }, []);

  /** Reveals the video on its first "playing" event as a fallback for onLoadedData —
   * some browsers never fire loadeddata for a video whose src carries a #t= media
   * fragment, which would otherwise leave the cell stuck on the poster indefinitely.
   * @returns {void}
   */
  const onVideoPlaying = useCallback((_event, playing) => {
    if (playing) setIsVideoLoaded(true);
  }, []);

  /** @returns {void} */
  const onVideoClick = useCallback(() => {
    onVideoToggle(index);
    onCellClick?.({
      action: "Video Clicked",
      item: {
        item_name: textContent || cell.title,
        item_position: index,
        item_row: Math.floor(index / colCount) + 1,
        item_column: (index % colCount) + 1,
        redirection_url: cell.linkUrl,
      },
    });
  }, [onVideoToggle, index, onCellClick, textContent, cell.title, cell.linkUrl, colCount]);

  if (!imageUrl) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-grey-100 text-xs text-grey-500">Empty cell</div>
    );
  }

  const mediaElement =
    resolvedMediaType === "lottie" ? (
      <GridLottieComponent animationPath={imageUrl} isLoop={true} />
    ) : resolvedMediaType === "video" ? (
      <div className="relative h-full w-full">
        {!isVideoLoaded && cell.imageUrl && (
          <div className="absolute inset-0 z-10">
            <img
              src={cell.imageUrl}
              alt={accessibleLabel}
              className="h-full w-full object-cover"
              loading={isEager || isPriority ? "eager" : "lazy"}
              fetchPriority={isPriority ? "high" : undefined}
            />
          </div>
        )}
        <VideoPlayer
          customRef={localVideoRef}
          videoUrl={imageUrl}
          poster={cell.imageUrl}
          title={cell.alt || cell.title || "Video"}
          classes={`h-full w-full object-cover transition-opacity duration-300 ${isVideoLoaded ? "opacity-100" : "opacity-0"}`}
          isMuted={true}
          isLoop={true}
          isPlaysInline={true}
          preload="auto"
          onLoadedData={onLoadedData}
          onPlaying={onVideoPlaying}
          onClick={onVideoClick}
          isAutoPlay={true}
        />
      </div>
    ) : (
      <CustomImage
        src={imageUrl}
        alt={accessibleLabel}
        className="h-full w-full object-cover"
        width={100}
        height={100}
        isPriority={isPriority}
        loading={isEager || isPriority ? "eager" : "lazy"}
        fetchPriority={isPriority ? "high" : undefined}
      />
    );

  /** @param {React.MouseEvent} e @returns {void} */
  const onMediaMouseEnter = (e) => {
    if (!cellBorderThickness && !isMobile) {
      e.currentTarget.style.border = "2px solid rgba(255, 255, 255, 0.80)";
    }
    if (!isMobile) {
      e.currentTarget.style.boxShadow = "0 2px 16px 0 rgba(0, 0, 0, 0.12)";
    }
  };

  /** @param {React.MouseEvent} e @returns {void} */
  const onMediaMouseLeave = (e) => {
    if (!cellBorderThickness && !isMobile) {
      e.currentTarget.style.border = cellBorderStyle || "2px solid transparent";
    }
    if (!isMobile) {
      e.currentTarget.style.boxShadow = "none";
    }
  };

  const innerContent = (
    <div
      className="relative w-full overflow-hidden"
      style={{
        aspectRatio: aspectRatio ? `${aspectRatio}` : undefined,
        borderRadius: cellBorderRadius ?? 0,
        border: cellBorderStyle,
        transition: "border 0.2s ease, box-shadow 0.2s ease",
      }}
      onMouseEnter={onMediaMouseEnter}
      onMouseLeave={onMediaMouseLeave}
    >
      {mediaElement}
      {textEnabled && textContent && innerText && (
        <div
          className={`absolute left-0 right-0 ${verticalPositionClass}`}
          style={{
            paddingLeft: isMobile ? "8px" : "15px",
            paddingRight: isMobile ? "8px" : "15px",
            ...(verticalAlignment === "top"
              ? { paddingTop: isMobile ? "8px" : "15px" }
              : verticalAlignment === "center"
                ? {}
                : { paddingBottom: isMobile ? "8px" : "15px" }),
          }}
        >
          <p className="break-words" style={{ ...textStyle, color: textStyle.color || "#ffffff" }}>
            {textContent}
          </p>
        </div>
      )}
    </div>
  );

  const content = (
    <>
      {innerContent}
      {textEnabled && textContent && !innerText && (
        <CellCaption style={textStyle} wrapperStyle={{ marginTop: isMobile ? "8px" : "16px" }}>
          {textContent}
        </CellCaption>
      )}
    </>
  );

  /**
   * Fires Widget Clicked event when the cell link is clicked.
   * The navigation flag is gated so a drag-to-scroll inside a carousel — which ends in a
   * click the carousel cancels — doesn't leave the navigation skeleton on screen.
   * @param {React.MouseEvent} event - Click event from the cell anchor.
   * @returns {void}
   */
  const onCellLinkClick = (event) => {
    runOnNavigationIntent(event, () => dispatch(setIsNavigatingAction(true)));
    const isVideoCell = resolvedMediaType === "video";
    onCellClick?.({
      action: isVideoCell ? "Video Clicked" : "Image Clicked",
      item: {
        item_name: textContent || cell.title,
        title: cell.title,
        imageUrl: cell.imageUrl,
        linkUrl: cell.linkUrl,
        redirection_url: cell.linkUrl,
        item_position: index,
        position: index,
        item_row: Math.floor(index / colCount) + 1,
        item_column: (index % colCount) + 1,
        row: Math.floor(index / colCount) + 1,
        column: (index % colCount) + 1,
      },
    });
  };

  const linkLabel = accessibleLabel;

  return cell.linkUrl ? (
    <a
      href={cell.linkUrl}
      className="block h-full w-full"
      aria-label={isImageCell ? undefined : linkLabel}
      onClick={onCellLinkClick}
    >
      {content}
    </a>
  ) : (
    content
  );
};

GridCell.propTypes = {
  cell: PropTypes.shape({
    imageUrl: PropTypes.string,
    mediaUrl: PropTypes.string,
    mediaType: PropTypes.string,
    textContent: PropTypes.string,
    title: PropTypes.string,
    alt: PropTypes.string,
    linkUrl: PropTypes.string,
    background: PropTypes.shape({
      type: PropTypes.string,
      value: PropTypes.string,
    }),
  }).isRequired,
  index: PropTypes.number.isRequired,
  onVideoToggle: PropTypes.func.isRequired,
  videoRefs: PropTypes.shape({ current: PropTypes.object }).isRequired,
  columnCount: PropTypes.number,
  aspectRatio: PropTypes.number,
  cellBorderRadius: PropTypes.number,
  cellBorderStyle: PropTypes.string,
  cellBorderThickness: PropTypes.number,
  gridTextStyle: PropTypes.shape({
    textEnabled: PropTypes.bool,
    innerText: PropTypes.bool,
    fontSize: PropTypes.string,
    fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fontFamily: PropTypes.string,
    titleColor: PropTypes.string,
    horizontalAlignment: PropTypes.string,
    verticalAlignment: PropTypes.string,
  }),
  isMobile: PropTypes.bool.isRequired,
  isEager: PropTypes.bool,
  isPriority: PropTypes.bool,
};

export default GridCell;
